import {FormikProps} from 'formik'
import * as Yup from 'yup'

import useKeyPress from '../../../../../../commons/src/hooks/use-key-press'
import preventDoubleClick from '../../../../../../commons/src/services/prevent-double-click'
import {formatCurrency} from '../../../../libs/formatter'
import {isMixedValue, unlockMixedValuesLabel} from '../../../utils/form'
import {MixedFormData, SingleFormData, initialValues} from '../utils'

import {Infill, Provider} from '../../../../types'

import closeIcon from '../../../../../../commons/src/assets/icons/close.svg'

import Button from '../../../../../../commons/src/components/button'
import FieldLabel from '../../../../../../commons/src/components/field-label/FieldLabel'
import FieldNote from '../../../../../../commons/src/components/field-note'
import FormRow from '../../../../../../commons/src/components/form-row'
import Headline from '../../../../../../commons/src/components/headline'
import Icon from '../../../../../../commons/src/components/icon'
import InputField from '../../../../../../commons/src/components/input-field'
import NumberInputField from '../../../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../../../commons/src/components/paragraph'
import SelectField from '../../../../../../commons/src/components/select-field'
import SelectMenu from '../../../../../../commons/src/components/select-menu'
import MixedValueFormSection from '../../../../components/mixed-value-form-section'

type InfillOption = {
  label: string
  value: Infill
}

export const validationSchema = {
  name: Yup.string().label('Name'),
  type: Yup.string().label('Infill type').required(),
  basePrice: Yup.number()
    .label('Base price')
    .min(0)
    .when(['factor'], {
      is: (factor: number) => typeof factor === 'undefined',
      then: schema => schema.required('Either base price or percentage is required'),
    }),
  productionTimeFastOffset: Yup.number()
    .label('Minimum additional production time')
    .integer()
    .min(0)
    .required(),
  productionTimeSlowOffset: Yup.number()
    .label('Maximum additional production time')
    .integer()
    .required()
    .min(Yup.ref('productionTimeFastOffset')),
  factor: Yup.number()
    .label('Percentage')
    .when(['basePrice'], {
      is: (basePrice: number) => !basePrice,
      then: schema => schema.required('Either base price or percentage is required'),
    }),
}

type Props = {
  formik: FormikProps<SingleFormData> | FormikProps<MixedFormData>
  provider: Provider
}

const infillOptions: InfillOption[] = Object.values(Infill).map(infill => ({
  label: `${infill.replace('infill_', '')}%`,
  value: infill,
}))

const InfillsForm = ({formik, provider}: Props) => {
  useKeyPress(
    preventDoubleClick(() => formik.handleSubmit()),
    'Enter'
  )

  return (
    <form onSubmit={formik.handleSubmit}>
      {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
      <Headline size="s" label="General" />
      <FormRow>
        <FieldNote appearance="error" note={formik.submitCount > 0 && formik.errors.name}>
          <FieldLabel label="Name" addendum="optional">
            <InputField
              placeholder="for your internal reference only"
              name="name"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.name)}
              value={isMixedValue(formik.values.name) ? initialValues.name : formik.values.name}
              onChange={(value, name, event) => formik.handleChange(event)}
              onBlur={formik.handleBlur}
              action={
                isMixedValue(formik.values.name) ? (
                  <Button
                    hierarchy="ghost-input-action"
                    icon={<Icon block source={closeIcon} title={unlockMixedValuesLabel} />}
                    onClick={() => formik.setFieldValue('name', initialValues.name)}
                  />
                ) : undefined
              }
            />
          </FieldLabel>
        </FieldNote>
      </FormRow>
      <FormRow layout="half-half">
        <FieldLabel label="Infill percentage">
          <FieldNote appearance="error" note={formik.submitCount > 0 && formik.errors.type}>
            <SelectField
              name="type"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.type)}
              value={infillOptions.find(({value}) => value === formik.values.type)}
              menu={<SelectMenu values={infillOptions} />}
              onChange={infillOption => {
                formik.setFieldValue('type', (infillOption as InfillOption).value)
              }}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
        </FieldLabel>
      </FormRow>
      <Headline size="s" label="Additional production time" className="u-margin-top-l" />
      <Paragraph minor size="s">
        The increase will be added on top of your standard production time.
      </Paragraph>
      <MixedValueFormSection
        locked={
          isMixedValue(formik.values.productionTimeFastOffset) ||
          isMixedValue(formik.values.productionTimeSlowOffset)
        }
        onClick={() => {
          formik.setFieldValue(
            'productionTimeFastOffset',
            isMixedValue(formik.values.productionTimeFastOffset)
              ? initialValues.productionTimeFastOffset
              : formik.values.productionTimeFastOffset
          )
          formik.setFieldValue(
            'productionTimeSlowOffset',
            isMixedValue(formik.values.productionTimeSlowOffset)
              ? initialValues.productionTimeSlowOffset
              : formik.values.productionTimeSlowOffset
          )
        }}
        unlockButtonLabel={unlockMixedValuesLabel}
      >
        <FormRow layout="half-half" divider="-">
          <FieldNote
            appearance="error"
            note={formik.submitCount > 0 && formik.errors.productionTimeFastOffset}
          >
            <NumberInputField
              placeholder="min"
              name="productionTimeFastOffset"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.productionTimeFastOffset)}
              value={
                isMixedValue(formik.values.productionTimeFastOffset)
                  ? initialValues.productionTimeFastOffset
                  : formik.values.productionTimeFastOffset
              }
              suffix="business day(s)"
              appearance={
                formik.submitCount > 0 && !!formik.errors.productionTimeFastOffset
                  ? 'error'
                  : undefined
              }
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
          <FieldNote
            appearance="error"
            note={formik.submitCount > 0 && formik.errors.productionTimeSlowOffset}
          >
            <NumberInputField
              placeholder="max"
              name="productionTimeSlowOffset"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.productionTimeSlowOffset)}
              value={
                isMixedValue(formik.values.productionTimeSlowOffset)
                  ? initialValues.productionTimeSlowOffset
                  : formik.values.productionTimeSlowOffset
              }
              suffix="business day(s)"
              appearance={
                formik.submitCount > 0 && !!formik.errors.productionTimeSlowOffset
                  ? 'error'
                  : undefined
              }
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
        </FormRow>
      </MixedValueFormSection>
      <Headline size="s" label="Additional production cost" className="u-margin-top-l" />
      <Paragraph minor size="s">
        Add either a percentage or base price per file on top of your standard pricing. Both can be
        combined.
      </Paragraph>
      <FormRow layout="half-half">
        <FieldLabel label="Percentage of standard price">
          <FieldNote appearance="error" note={formik.submitCount > 0 && formik.errors.factor}>
            <NumberInputField
              allowDecimals
              allowOnly2Decimals
              name="factor"
              suffix="%"
              appearance={formik.submitCount > 0 && !!formik.errors.factor ? 'error' : undefined}
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.factor)}
              value={
                isMixedValue(formik.values.factor) ? initialValues.factor : formik.values.factor
              }
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
              action={
                isMixedValue(formik.values.factor) ? (
                  <Button
                    hierarchy="ghost-input-action"
                    icon={<Icon block source={closeIcon} title={unlockMixedValuesLabel} />}
                    onClick={() => formik.setFieldValue('factor', initialValues.factor)}
                  />
                ) : undefined
              }
            />
          </FieldNote>
        </FieldLabel>
        <FieldLabel label="Base price per file">
          <FieldNote appearance="error" note={formik.submitCount > 0 && formik.errors.basePrice}>
            <NumberInputField
              allowDecimals
              name="basePrice"
              suffix={formatCurrency(provider.currency)}
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.basePrice)}
              value={
                isMixedValue(formik.values.basePrice)
                  ? initialValues.basePrice
                  : formik.values.basePrice
              }
              appearance={formik.submitCount > 0 && !!formik.errors.basePrice ? 'error' : undefined}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
              action={
                isMixedValue(formik.values.basePrice) ? (
                  <Button
                    hierarchy="ghost-input-action"
                    icon={<Icon block source={closeIcon} title={unlockMixedValuesLabel} />}
                    onClick={() => formik.setFieldValue('basePrice', initialValues.basePrice)}
                  />
                ) : undefined
              }
            />
          </FieldNote>
        </FieldLabel>
      </FormRow>
    </form>
  )
}

export default InfillsForm
